import { useEffect, useState } from 'react';
import { useSurveyStore } from '../../stores/surveyStore';
import { t } from '../../i18n';
import { sections, getQuestionsBySection } from '@nordcrest/shared';
import type { Question, AIFollowUpQuestion } from '@nordcrest/shared';
import { validateAnswer } from '../../lib/validation';
import * as api from '../../lib/api';
import { Button, Card, CardContent, ProgressBar, Spinner } from '../ui';
import { QuestionRenderer } from './QuestionRenderer';
import { IntroScreen } from './IntroScreen';
import { AILoadingScreen } from './AILoadingScreen';
import { CompletionScreen } from './CompletionScreen';

// Type guard to check if question is AIFollowUpQuestion
function isAIFollowUpQuestion(q: Question | AIFollowUpQuestion): q is AIFollowUpQuestion {
  return 'questionText' in q;
}

export function SurveyContainer() {
  const store = useSurveyStore();
  const [validationError, setValidationError] = useState<string | undefined>();

  const {
    surveyId,
    phase,
    language,
    currentSection,
    currentQuestionIndex,
    answers,
    aiFollowUpQuestions,
    aiFollowUpAnswers,
    isLoading,
    isSaving,
    error,
  } = store;

  // Initialize survey on mount
  useEffect(() => {
    if (phase === 'intro' && !surveyId) {
      // Will create survey when user clicks start
    }
  }, [phase, surveyId]);

  const handleStart = async () => {
    store.setLoading(true);
    store.setError(null);

    try {
      // Get UTM params from URL
      const params = new URLSearchParams(window.location.search);
      const survey = await api.createSurvey({
        language,
        source: params.get('source') || undefined,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined,
      });

      store.setSurveyId(survey.id);
      store.setPhase('questions');
      store.setCurrentSection(1);
    } catch (err) {
      store.setError(t('errors.network', language));
    } finally {
      store.setLoading(false);
    }
  };

  const handleLanguageToggle = () => {
    const newLang = language === 'sv' ? 'en' : 'sv';
    store.setLanguage(newLang);
    if (surveyId) {
      api.updateSurvey(surveyId, { language: newLang }).catch(console.error);
    }
  };

  const getCurrentQuestion = () => {
    if (phase === 'ai-questions') {
      return aiFollowUpQuestions[currentQuestionIndex] || null;
    }
    const questions = getQuestionsBySection(currentSection);
    return questions[currentQuestionIndex] || null;
  };

  const getCurrentAnswer = () => {
    const question = getCurrentQuestion();
    if (!question) return null;

    if (phase === 'ai-questions') {
      return aiFollowUpAnswers[question.id]?.value ?? null;
    }

    return answers[question.id]?.value ?? null;
  };

  const handleAnswerChange = (value: string | string[] | number | boolean | null) => {
    const question = getCurrentQuestion();
    if (!question) return;

    setValidationError(undefined);

    if (phase === 'ai-questions') {
      store.setAIFollowUpAnswer(question.id, value);
    } else {
      store.setAnswer(question.id, value);
    }
  };

  const saveCurrentAnswers = async () => {
    if (!surveyId) return;

    const answersToSave = Object.entries(answers)
      .filter(([_, v]) => v.value !== null && v.value !== undefined)
      .map(([questionId, v]) => ({
        questionId,
        value: v.value,
      }));

    if (answersToSave.length > 0) {
      await api.submitAnswers(surveyId, { answers: answersToSave });
    }
  };

  const handleNext = async () => {
    const question = getCurrentQuestion();
    if (!question) return;

    // Validate current answer
    const currentValue = getCurrentAnswer();
    const questionForValidation = isAIFollowUpQuestion(question)
      ? {
          id: question.id,
          type: question.questionType,
          required: true,
          text: { sv: question.questionText, en: question.questionText },
        } as Question
      : question;

    const validation = validateAnswer(currentValue, questionForValidation, language);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    store.setSaving(true);

    try {
      if (phase === 'ai-questions') {
        // Save AI follow-up answer
        await api.submitFollowUpAnswer(question.id, currentValue as string | string[] | number | null);

        // Check if more AI questions
        if (currentQuestionIndex < aiFollowUpQuestions.length - 1) {
          store.setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          // Move to contact section
          store.setPhase('contact');
          store.setCurrentSection(4);
          store.setCurrentQuestionIndex(0);
        }
      } else {
        // Regular questions
        const sectionQuestions = getQuestionsBySection(currentSection);

        if (currentQuestionIndex < sectionQuestions.length - 1) {
          // More questions in section
          store.setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else if (currentSection === 2) {
          // End of section 2, trigger AI questions
          await saveCurrentAnswers();
          store.setPhase('ai-loading');
          store.setCurrentQuestionIndex(0);

          // Load AI questions
          const followUps = await api.getFollowUpQuestions(surveyId!);
          store.setAIFollowUpQuestions(followUps);
          store.setPhase('ai-questions');
        } else if (currentSection === 4) {
          // Contact section complete - handled by handleSubmit
        } else {
          // Move to next section
          await saveCurrentAnswers();
          store.setCurrentSection(currentSection + 1);
          store.setCurrentQuestionIndex(0);
        }
      }
    } catch (err) {
      store.setError(t('errors.generic', language));
    } finally {
      store.setSaving(false);
    }
  };

  const handlePrev = () => {
    setValidationError(undefined);

    if (phase === 'ai-questions') {
      if (currentQuestionIndex > 0) {
        store.setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else {
        // Go back to section 2
        store.setPhase('questions');
        store.setCurrentSection(2);
        const section2Questions = getQuestionsBySection(2);
        store.setCurrentQuestionIndex(section2Questions.length - 1);
      }
    } else if (phase === 'contact') {
      if (currentQuestionIndex > 0) {
        store.setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else {
        // Go back to AI questions
        store.setPhase('ai-questions');
        store.setCurrentQuestionIndex(aiFollowUpQuestions.length - 1);
      }
    } else {
      if (currentQuestionIndex > 0) {
        store.setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else if (currentSection > 1) {
        store.setCurrentSection(currentSection - 1);
        const prevSectionQuestions = getQuestionsBySection(currentSection - 1);
        store.setCurrentQuestionIndex(prevSectionQuestions.length - 1);
      }
    }
  };

  const handleSubmit = async () => {
    if (!surveyId) return;

    store.setSaving(true);
    store.setError(null);

    try {
      // Collect contact info from answers
      const contactData = {
        surveyId,
        email: String(answers.contact_email?.value || ''),
        firstName: String(answers.contact_first_name?.value || ''),
        lastName: String(answers.contact_last_name?.value || ''),
        phone: String(answers.contact_phone?.value || '') || undefined,
        company: String(answers.contact_company?.value || ''),
        wantsCallback: answers.contact_callback?.value === true,
        wantsNewsletter: answers.contact_newsletter?.value === true,
        gdprConsent: answers.contact_gdpr?.value === true,
      };

      await api.submitSurveyAndGenerateReport(surveyId, contactData);
      store.setComplete(contactData.wantsCallback);
    } catch (err) {
      store.setError(t('errors.generic', language));
    } finally {
      store.setSaving(false);
    }
  };

  const handleReset = () => {
    store.reset();
    window.location.reload();
  };

  // Render based on phase
  if (phase === 'intro') {
    return (
      <IntroScreen
        language={language}
        onStart={handleStart}
        onLanguageToggle={handleLanguageToggle}
        isLoading={isLoading}
      />
    );
  }

  if (phase === 'ai-loading') {
    return <AILoadingScreen language={language} />;
  }

  if (phase === 'complete') {
    return (
      <CompletionScreen
        language={language}
        wantsCallback={store.wantsCallback}
        onReset={handleReset}
      />
    );
  }

  const question = getCurrentQuestion();
  const currentAnswer = getCurrentAnswer();
  const progress = store.getProgress();

  // Determine section info
  let sectionInfo = sections.find((s) => s.id === currentSection);
  if (phase === 'ai-questions') {
    sectionInfo = sections.find((s) => s.id === 3);
  } else if (phase === 'contact') {
    sectionInfo = sections.find((s) => s.id === 4);
  }

  // Determine if this is the last question
  const isLastQuestion = phase === 'contact' && currentQuestionIndex === getQuestionsBySection(4).length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold text-primary-800">
              {t('survey.title', language)}
            </h1>
            <button
              onClick={handleLanguageToggle}
              className="text-sm text-gray-600 hover:text-primary-600"
            >
              {t('language.switchTo', language)}
            </button>
          </div>
          <ProgressBar percentage={progress.percentage} />
          <p className="text-sm text-gray-500 mt-2">
            {sectionInfo && (
              <>
                {t('survey.section', language, {
                  current: sectionInfo.id,
                  total: 4,
                  title: sectionInfo.title[language],
                })}
              </>
            )}
          </p>
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardContent>
            {question ? (
              isAIFollowUpQuestion(question) ? (
                <div className="py-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {question.questionText}
                    <span className="text-red-500 ml-1">*</span>
                  </h2>
                  {question.options ? (
                    <div className="space-y-3">
                      {question.options.map((option: any) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleAnswerChange(option.value)}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            currentAnswer === option.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300'
                          }`}
                        >
                          <span className="font-medium">{option.label[language]}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={(currentAnswer as string) || ''}
                      onChange={(e) => handleAnswerChange(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  )}
                  {validationError && (
                    <p className="text-sm text-red-600 mt-2">{validationError}</p>
                  )}
                </div>
              ) : (
                <QuestionRenderer
                  question={question as Question}
                  value={currentAnswer}
                  onChange={handleAnswerChange}
                  language={language}
                  error={validationError}
                />
              )
            ) : (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="secondary"
            onClick={handlePrev}
            disabled={phase === 'questions' && currentSection === 1 && currentQuestionIndex === 0}
          >
            {t('common.previous', language)}
          </Button>

          {isLastQuestion ? (
            <Button onClick={handleSubmit} isLoading={isSaving}>
              {t('common.submit', language)}
            </Button>
          ) : (
            <Button onClick={handleNext} isLoading={isSaving}>
              {t('common.next', language)}
            </Button>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
